import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { io } from 'socket.io-client';
import Select from 'react-select';
import { API_URL, API_TOKEN, api, classifyTrainType, trainTypeIcons, trainTypeColors, attributeLabels } from './trainUtils';
import BaureiheBadge from './BaureiheBadge';
import '../css/trainJourney.css';

// Unique identity per train journey — backend creates a new stationRequest per call,
// but the journey itself stays the same. Use journeyUUID with fallbacks.
const journeyKey = (j) => j?.journey?.journeyUUID || j?.UUID || j?.trainUUID || j?.journeyID || j?.stationRequest?.stationRequestUUID;

// Merge new journeys into existing list: replace by key if present, otherwise append.
// `position` decides where new (non-existing) entries are inserted: 'top' or 'bottom'.
const mergeJourneys = (existing, incoming, position) => {
  const idxByKey = new Map();
  existing.forEach((j, i) => idxByKey.set(journeyKey(j), i));
  const next = [...existing];
  const fresh = [];
  for (const j of incoming) {
    const k = journeyKey(j);
    if (idxByKey.has(k)) {
      next[idxByKey.get(k)] = j;
    } else {
      fresh.push(j);
      idxByKey.set(k, position === 'top' ? -1 : next.length + fresh.length - 1);
    }
  }
  if (fresh.length === 0) return next;
  return position === 'top' ? [...fresh, ...next] : [...next, ...fresh];
};

/**
 * Timetable list for one station.
 *
 * Two modes:
 *  - uncontrolled (no `journeys` prop): fetches, polls and subscribes to the socket itself.
 *    This is the original behaviour of the standalone timetable column.
 *  - controlled (`journeys` supplied): renders only. The map's station panel owns the data
 *    there, because it has to follow the map's operating day and point in time.
 *
 * `stationUUID` pins the station and hides the selector, `compact` switches to the narrow
 * panel layout. Without any of these props the component behaves exactly as before.
 */
const JourneyList = ({
  onJourneySelect, selectedJourneyId,
  stationUUID, journeys: controlledJourneys, loading: controlledLoading,
  timetableType: controlledType, compact = false,
}) => {
  const controlled = Array.isArray(controlledJourneys);
  const [stations, setStations] = useState([]);
  const [trainTypes, setTrainTypes] = useState([]);
  const [selectedStation, setSelectedStation] = useState(stationUUID || '');
  const [internalType, setInternalType] = useState('arrival');
  const timetableType = controlledType || internalType;
  const setTimetableType = controlledType ? () => {} : setInternalType;
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [uniqueJourneys, setUniqueJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setHasMore] = useState(true);
  const [activeFilters, setActiveFilters] = useState(new Set(['fern', 'sbahn', 'regio', 'ubahn', 'tram', 'bus']));
  const [selectedTrainType, setSelectedTrainType] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [expandedAttributes, setExpandedAttributes] = useState(new Set());
  const [showPast, setShowPast] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const socketRef = useRef(null);
  const prevStationRef = useRef(null);
  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const useCustomDateRef = useRef(false);

  const stationOptions = useMemo(() =>
    stations.map(s => ({ value: s.UUID, label: s.stationName })),
    [stations]
  );

  const selectedOption = stationOptions.find(o => o.value === selectedStation) || null;

  const formatTime = (time) => {
    const date = new Date(time);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Check if delay is greater than 5 minutes
  const isDelayed = (scheduled, actual) => {
    if (!scheduled || !actual) return false;
    return new Date(actual) - new Date(scheduled) > 359000;
  };

  // Get the relevant times based on tab
  const getTimeFields = (journey) => {
    const sr = journey.stationRequest;
    if (timetableType === 'departure') {
      return {
        scheduled: sr?.departureTime_scheduled,
        actual: sr?.departureTime_actual,
      };
    }
    return {
      scheduled: sr?.arrivalTime_scheduled,
      actual: sr?.arrivalTime_actual,
    };
  };

  const showTime = (journey) => {
    const { scheduled, actual } = getTimeFields(journey);
    if (!scheduled) return <div className="scheduled-time">--:--</div>;
    const delayed = isDelayed(scheduled, actual);
    return (
      <div className="arrival-time">
        <div className="scheduled-time">{formatTime(scheduled)}</div>
        {actual && (
          <span className={`actual-time ${delayed ? 'delayed' : 'on-time'}`}>
            ({formatTime(actual)})
          </span>
        )}
      </div>
    );
  };

  // Tick every 30s to update past train filtering
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Check if a train is in the past (arrival/departure > 5 min ago)
  const isPastTrain = useCallback((journey) => {
    const sr = journey.stationRequest;
    const time = timetableType === 'arrival'
      ? (sr?.arrivalTime_actual || sr?.arrivalTime_scheduled)
      : (sr?.departureTime_actual || sr?.departureTime_scheduled);
    if (!time) return false;
    return (now - new Date(time)) > 5 * 60 * 1000;
  }, [now, timetableType]);

  // Keep ref in sync for socket handler
  useEffect(() => { useCustomDateRef.current = useCustomDate; }, [useCustomDate]);

  // Follow a pinned station from the outside
  useEffect(() => {
    if (stationUUID) setSelectedStation(stationUUID);
  }, [stationUUID]);

  // Fetch available stations and train types on mount
  useEffect(() => {
    if (controlled) return;
    // The endpoint pages with a default of 50 — the selector needs every station
    api.get('/v1/stations', { params: { limit: 5000 } })
      .then(res => {
        const named = res.data.filter(s => s.stationName && s.stationName.trim() !== '');
        setStations(named);
        if (named.length > 0 && !stationUUID) {
          setSelectedStation(named[0].UUID);
        }
      })
      .catch(err => console.error('Error fetching stations:', err));

    api.get('/v1/train-types')
      .then(res => setTrainTypes(res.data))
      .catch(err => console.error('Error fetching train types:', err));
  // Mount-only: controlled and stationUUID do not change for a given mount, and re-fetching
  // the station list on every station change would be pure waste
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Connect Socket.IO once on mount
  useEffect(() => {
    if (controlled) return undefined;
    const socket = io(API_URL, {
      transports: ['websocket'],
      auth: { token: API_TOKEN },
    });
    socketRef.current = socket;

    socket.on('timetable_update', (data) => {
      const newJourneys = Array.isArray(data) ? data : [data];
      // In live mode, automatically add new trains at the top
      if (!useCustomDateRef.current) {
        setUniqueJourneys(prev => mergeJourneys(prev, newJourneys, 'top'));
      }
    });

    return () => {
      socket.disconnect();
    };
  // Mount-only: one socket per mount, and `controlled` is fixed for the lifetime
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe / unsubscribe when station changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedStation) return;

    // Unsubscribe from previous station
    if (prevStationRef.current) {
      socket.emit('unsubscribe_station', { stationUUID: prevStationRef.current });
    }

    // Clear list and subscribe to new station
    setUniqueJourneys([]);
    socket.emit('subscribe_station', { stationUUID: selectedStation });
    prevStationRef.current = selectedStation;
  }, [selectedStation]);

  // Fetch timetable when station, type, or custom date changes
  useEffect(() => {
    if (controlled) return;
    if (!selectedStation) return;
    setUniqueJourneys([]);
    setHasMore(true);
    offsetRef.current = 0;
    loadingRef.current = true;
    setLoading(true);

    const params = {
      sort_by: timetableType === 'arrival' ? 'arrival' : 'departure',
      limit: 50,
      offset: 0,
    };
    if (useCustomDate && customDate) {
      params.start = customDate;
    }
    api.get(`/v1/stations/${selectedStation}/timetable`, { params })
      .then(res => {
        setUniqueJourneys(res.data);
        setHasMore(res.data.length >= 50);
        offsetRef.current = res.data.length;
      })
      .catch(err => console.error('Error fetching journeys:', err))
      .finally(() => { loadingRef.current = false; setLoading(false); });
  }, [controlled, selectedStation, timetableType, useCustomDate, customDate]);

  // Poll for new trains every 30s in live mode
  useEffect(() => {
    if (controlled) return undefined;
    if (!selectedStation || useCustomDate) return undefined;
    const interval = setInterval(() => {
      const params = {
        sort_by: timetableType === 'arrival' ? 'arrival' : 'departure',
        limit: 50,
        offset: 0,
      };
      api.get(`/v1/stations/${selectedStation}/timetable`, { params })
        .then(res => {
          setUniqueJourneys(prev => mergeJourneys(prev, res.data, 'top'));
        })
        .catch(err => console.error('Polling error:', err));
    }, 30000);
    return () => clearInterval(interval);
  }, [controlled, selectedStation, timetableType, useCustomDate]);

  // Load next 50 journeys
  const loadMore = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    const params = {
      sort_by: timetableType === 'arrival' ? 'arrival' : 'departure',
      limit: 50,
      offset: offsetRef.current,
    };
    if (useCustomDate && customDate) {
      params.start = customDate;
    }

    api.get(`/v1/stations/${selectedStation}/timetable`, { params })
      .then(res => {
        const newData = res.data;
        setHasMore(newData.length >= 50);
        offsetRef.current += newData.length;
        setUniqueJourneys(prev => mergeJourneys(prev, newData, 'bottom'));
      })
      .catch(err => console.error('Error fetching journeys:', err))
      .finally(() => { loadingRef.current = false; setLoading(false); });
  }, [selectedStation, timetableType, useCustomDate, customDate]);

  // Scroll handler for infinite loading
  const handleScroll = useCallback((e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMore();
    }
  }, [loadMore]);



  const filterTypes = ['fern', 'sbahn', 'regio', 'ubahn', 'tram', 'bus'];
  const allTypesActive = filterTypes.every(t => activeFilters.has(t));
  const hasActiveFilters = !allTypesActive || !!selectedTrainType || useCustomDate;
  const showFilters = filtersOpen || hasActiveFilters;

  const toggleFilter = (type) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const trainTypeNameOptions = useMemo(() =>
    trainTypes
      .filter(t => t.trainTypeName && t.trainTypeName !== 'undefiniert')
      .map(t => ({ value: t.trainTypeName, label: t.trainTypeName }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [trainTypes]
  );

  const sourceJourneys = controlled ? controlledJourneys : uniqueJourneys;

  const filteredJourneys = useMemo(() => {
    const filtered = sourceJourneys.filter(j => {
      if (!activeFilters.has(classifyTrainType(j))) return false;
      if (selectedTrainType && j.trainTypeName !== selectedTrainType) return false;
      return true;
    });
    // Sort descending: next arrivals at top, future at bottom
    filtered.sort((a, b) => {
      const timeA = timetableType === 'arrival'
        ? (a.stationRequest?.arrivalTime_scheduled)
        : (a.stationRequest?.departureTime_scheduled);
      const timeB = timetableType === 'arrival'
        ? (b.stationRequest?.arrivalTime_scheduled)
        : (b.stationRequest?.departureTime_scheduled);
      return new Date(timeA) - new Date(timeB);
    });
    return filtered;
  }, [sourceJourneys, activeFilters, selectedTrainType, timetableType]);

  const pastCount = useMemo(() =>
    filteredJourneys.filter(j => isPastTrain(j)).length,
    [filteredJourneys, isPastTrain]
  );

  const visibleJourneys = useMemo(() =>
    // In the panel the map already decides which moment is shown — hiding "past" trains
    // there would silently drop what the user just asked for
    (controlled || useCustomDate || showPast)
      ? filteredJourneys
      : filteredJourneys.filter(j => !isPastTrain(j)),
    [filteredJourneys, showPast, isPastTrain, useCustomDate, controlled]
  );

  const isLoading = controlled ? !!controlledLoading : loading;

  return (
    <div className={compact ? 'journey-list-compact' : undefined}>
      {/* Station selector — hidden when the station is pinned from the outside */}
      {!stationUUID && (
      <div className="station-selector">
        <div className="station-select-row">
          <i className="fa fa-map-marker station-select-icon"></i>
          <Select
            options={stationOptions}
            value={selectedOption}
            onChange={(opt) => opt && setSelectedStation(opt.value)}
            placeholder="Station suchen..."
            isSearchable
            noOptionsMessage={() => 'Keine Station gefunden'}
            loadingMessage={() => 'Laden...'}
            isLoading={stations.length === 0}
            styles={{
              control: (base, state) => ({
                ...base,
                background: 'var(--input-bg)',
                borderColor: state.isFocused ? 'var(--primary-color)' : 'var(--border-color)',
                borderRadius: 8,
                padding: '2px 4px',
                fontSize: '0.9rem',
                boxShadow: 'none',
                '&:hover': { borderColor: 'var(--primary-color)' },
              }),
              menu: (base) => ({
                ...base,
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                zIndex: 10,
              }),
              option: (base, state) => ({
                ...base,
                background: state.isFocused ? 'var(--bg-hover)' : 'transparent',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                '&:active': { background: 'var(--bg-subtle)' },
              }),
              singleValue: (base) => ({
                ...base,
                color: 'var(--text-primary)',
              }),
              input: (base) => ({
                ...base,
                color: 'var(--text-primary)',
              }),
              placeholder: (base) => ({
                ...base,
                color: 'var(--text-muted)',
              }),
              dropdownIndicator: (base) => ({
                ...base,
                color: 'var(--text-muted)',
                '&:hover': { color: 'var(--text-primary)' },
              }),
              indicatorSeparator: () => ({ display: 'none' }),
            }}
          />
        </div>
      </div>
      )}

      {/* Filter toggle */}
      <button
        className={`filters-toggle ${showFilters ? 'open' : ''} ${hasActiveFilters ? 'has-active' : ''}`}
        onClick={() => setFiltersOpen(o => !o)}
        disabled={hasActiveFilters}
        title={hasActiveFilters ? 'Filter aktiv — zum Schließen alle Filter zurücksetzen' : (showFilters ? 'Filter ausblenden' : 'Filter anzeigen')}
      >
        <i className="fa fa-filter"></i>
        <span>Filter</span>
        {hasActiveFilters && <span className="filters-toggle-badge">aktiv</span>}
        <i className={`fa fa-chevron-${showFilters ? 'up' : 'down'} filters-toggle-chevron`}></i>
      </button>

      {showFilters && (
      <>
      {/* Optional datetime picker — in the panel the map's time controls own this */}
      {!controlled && (
      <div className="datetime-picker-row">
        <label className="datetime-toggle">
          <input
            type="checkbox"
            checked={useCustomDate}
            onChange={(e) => {
              setUseCustomDate(e.target.checked);
              if (!e.target.checked) setCustomDate('');
            }}
          />
          <span>Zeitreise</span>
        </label>
        {useCustomDate && (
          <input
            type="datetime-local"
            className="datetime-input"
            value={customDate}
            onChange={(e) => setCustomDate(e.target.value)}
          />
        )}
      </div>
      )}

      {/* Train type filter */}
      <div className="type-filter-row">
        {filterTypes.map(type => (
          <button
            key={type}
            className={`type-filter-btn ${activeFilters.has(type) ? 'active' : ''}`}
            onClick={() => toggleFilter(type)}
            title={type}
          >
            {trainTypeIcons[type]}
          </button>
        ))}
        <div className="type-filter-select">
          <Select
            options={trainTypeNameOptions}
            value={trainTypeNameOptions.find(o => o.value === selectedTrainType) || null}
            onChange={(opt) => setSelectedTrainType(opt ? opt.value : null)}
            placeholder="Alle Typen"
            isClearable
            isSearchable
            noOptionsMessage={() => 'Kein Typ gefunden'}
            styles={{
              control: (base, state) => ({
                ...base,
                background: 'var(--input-bg)',
                borderColor: state.isFocused ? 'var(--primary-color)' : 'var(--border-color)',
                borderRadius: 8,
                minHeight: 38,
                fontSize: '0.82rem',
                boxShadow: 'none',
                '&:hover': { borderColor: 'var(--primary-color)' },
              }),
              menu: (base) => ({
                ...base,
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: 8,
                zIndex: 10,
              }),
              option: (base, state) => ({
                ...base,
                background: state.isFocused ? 'var(--bg-hover)' : 'transparent',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                fontSize: '0.82rem',
                '&:active': { background: 'var(--bg-subtle)' },
              }),
              singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
              input: (base) => ({ ...base, color: 'var(--text-primary)' }),
              placeholder: (base) => ({ ...base, color: 'var(--text-muted)' }),
              dropdownIndicator: (base) => ({
                ...base, color: 'var(--text-muted)', padding: '4px',
                '&:hover': { color: 'var(--text-primary)' },
              }),
              clearIndicator: (base) => ({
                ...base, color: 'var(--text-muted)', padding: '4px',
                '&:hover': { color: 'var(--primary-color)' },
              }),
              indicatorSeparator: () => ({ display: 'none' }),
            }}
          />
        </div>
      </div>
      </>
      )}

      {/* Arrival / Departure tabs — the panel has its own tabs for this */}
      {!controlled && (
      <div className="timetable-tabs">
        <button
          className={`timetable-tab ${timetableType === 'arrival' ? 'active' : ''}`}
          onClick={() => setTimetableType('arrival')}
        >
          <i className="fa fa-sign-in"></i> Ankünfte
        </button>
        {/* Departure tab — hidden for now, will be enabled later
        <button
          className={`timetable-tab ${timetableType === 'departure' ? 'active' : ''}`}
          onClick={() => setTimetableType('departure')}
        >
          <i className="fa fa-sign-out"></i> Abfahrten
        </button>
        */}
      </div>
      )}

      {/* Past trains toggle */}
      {!controlled && !useCustomDate && pastCount > 0 && (
        <button className="past-trains-btn" onClick={() => setShowPast(prev => !prev)}>
          <i className={`fa ${showPast ? 'fa-eye-slash' : 'fa-eye'}`}></i>
          {showPast ? ' Vergangene Züge ausblenden' : ` ${pastCount} vergangene Züge einblenden`}
        </button>
      )}

      {/* Journey list */}
      <ul
        className="list-group journey-list"
        style={compact ? undefined : { maxHeight: '560px', overflowY: 'auto' }}
        onScroll={controlled ? undefined : handleScroll}
      >
        {visibleJourneys.length === 0 && !isLoading && (
          <li className="journey-empty">Keine Züge verfügbar</li>
        )}
        {visibleJourneys.map((journey, index) => {
          const type = classifyTrainType(journey);
          const displayName = journey.trainLineName || journey.trainName || journey.trainTypeName || '?';
          const rawSubtitle = journey.trainName_middle || journey.trainLineNumber || '';
          const subtitle = rawSubtitle !== displayName ? rawSubtitle : '';
          const sr = journey.stationRequest;
          const track = sr?.track_actual || sr?.track_scheduled;
          const messages = (sr?.messages || []).slice().sort((a, b) => (b.priority || 0) - (a.priority || 0));
          const attributes = journey.journey?.journeyAttributes || [];
          const hasMessages = messages.length > 0;
          const hasHighPriority = messages.some(m => m.priority >= 2);
          const haltEntfaellt = messages.some(m => m.messageText?.includes('Halt entfällt'));
          const cardId = journeyKey(journey) || index;
          const isExpanded = expandedMessages.has(cardId);
          const isAttrExpanded = expandedAttributes.has(cardId);
          const firstMessage = messages[0];
          return (
            <React.Fragment key={cardId}>
              <li
                className={`list-group-item shadow-sm mb-3 p-3 rounded train-card ${selectedJourneyId && cardId === selectedJourneyId ? 'train-card-selected' : ''}`}
                style={{
                  borderLeft: `8px solid ${trainTypeColors[type]}`,
                  transition: 'background-color 0.3s ease, transform 0.3s ease',
                  cursor: 'pointer',
                }}
                onClick={() => onJourneySelect && onJourneySelect(journey)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <span className="journey-list-icon-span mr-4">{trainTypeIcons[type]}</span>
                    <div>
                      <h5 className="mb-0">
                        {displayName}
                        {subtitle && <span className="train-subtitle"> ({subtitle})</span>}
                      </h5>
                      {journey.trainTerminus && (
                        <small className="text-muted">
                          {timetableType === 'arrival' ? 'Von' : 'Nach'}: {journey.trainTerminus}
                        </small>
                      )}
                      {(() => {
                        const series = journey.trainSeries || [];
                        const expected = series.find(s => s.trainSerieExpected === 1) || series[0];
                        return expected ? (
                          <div className="train-card-baureihe">
                            <BaureiheBadge serie={expected} size="sm" />
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div className="text-right">
                    {showTime(journey)}
                    {track && <div className="track-info">Gl. {track}</div>}
                  </div>
                </div>
                {haltEntfaellt && (
                  <div className="train-message-banner banner-cancel">
                    <div className="banner-header">
                      <span className="banner-label">
                        <i className="fa fa-times-circle"></i> Halt entfällt
                      </span>
                    </div>
                  </div>
                )}
                {hasMessages && (
                  <div
                    className={`train-message-banner ${hasHighPriority ? 'banner-warn' : 'banner-info'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedMessages(prev => {
                        const next = new Set(prev);
                        if (next.has(cardId)) next.delete(cardId);
                        else next.add(cardId);
                        return next;
                      });
                    }}
                  >
                    <div className="banner-header">
                      <span className="banner-label">
                        <i className={`fa ${hasHighPriority ? 'fa-exclamation-triangle' : 'fa-info-circle'}`}></i>
                        {hasHighPriority ? ' Warnung' : ' Meldung'}
                        {messages.length > 1 && ` (${messages.length})`}
                      </span>
                      <span className="banner-preview">{firstMessage?.messageText}</span>
                      <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} banner-toggle`}></i>
                    </div>
                    {isExpanded && (
                      <div className="banner-messages">
                        {messages.map((msg, i) => (
                          <div key={msg.UUID || i} className={`banner-message ${msg.priority >= 2 ? 'priority-high' : ''}`}>
                            <div className="banner-message-text">{msg.messageText}</div>
                            {msg.created && (
                              <div className="banner-message-time">
                                {new Date(msg.created).toLocaleString('de-DE')}
                              </div>
                            )}
                            {msg.messageURL && (
                              <a
                                href={msg.messageURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="banner-message-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Mehr erfahren <i className="fa fa-external-link"></i>
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {attributes.length > 0 && (
                  <div
                    className="train-attributes-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedAttributes(prev => {
                        const next = new Set(prev);
                        if (next.has(cardId)) next.delete(cardId);
                        else next.add(cardId);
                        return next;
                      });
                    }}
                  >
                    <span className="attributes-toggle-label">
                      <i className="fa fa-tags"></i> Ausstattung ({attributes.length})
                      <i className={`fa fa-chevron-${isAttrExpanded ? 'up' : 'down'} banner-toggle`}></i>
                    </span>
                    {isAttrExpanded && (
                      <div className="train-attributes-list">
                        {attributes.map((attr, i) => {
                          const mapped = attributeLabels[attr.categorieKey];
                          return (
                            <span key={attr.UUID || i} className="train-attribute-badge">
                              <i className={`fa ${mapped?.icon || 'fa-info-circle'}`}></i>
                              {mapped?.label || attr.categorieValue || attr.categorieKey}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </li>
            </React.Fragment>
          );
        })}
        {isLoading && (
          <li className="journey-empty">
            <i className="fa fa-spinner fa-spin"></i> Laden...
          </li>
        )}
      </ul>

    </div>
  );
};

export default JourneyList;
